import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, Pencil, Plus, X } from 'lucide-react';

import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { InfoModal } from '@/components/feedback/InfoModal';
import { SlidePanel } from '@/components/feedback/SlidePanel';
import { toast } from '@/components/feedback/useToast';
import { useWriteGuard } from '@/shared/hooks/useWriteGuard';
import { formatPhoneForDisplay, normalizePhone, toPhoneInputValue } from '@/shared/utils/phone';

import {
  useDiseaseCodesQuery,
  useDiseaseGroupsQuery,
} from '../hooks/use-diagnosis-options';
import { useUpdatePatient } from '../hooks/use-patient-mutations';
import { usePatientQuery } from '../hooks/use-patients';
import { usePatientDetailStore } from '../store/patient-detail.store';
import {
  PatientApiError,
  type Patient,
  type PatientApiErrorCode,
  type PatientGender,
  type PatientGuardianRelationship,
  type PatientMedication,
  type UpdatePatientInput,
} from '../types/patient.types';
import { dashIfBlank, formatDate, formatDateTime, formatUid } from '../utils/format-patient';
import {
  buildUpdateInput,
  draftFromPatient,
  validateDraft,
  type DraftErrors,
  type DraftField,
  type DraftStaff,
  type PatientDraft,
} from '../utils/patient-edit-draft';
import {
  RESIDENCE_DETAIL_MAX,
  RESIDENCE_MAX,
  medicationRows,
  todayInKst,
} from '../utils/patient-field-rules';
import { PatientRegistrationStaffPicker } from './PatientRegistrationStaffPicker';
import { PatientStatusBadge } from './PatientStatusBadge';

// PRD 02 §3.6: 480 px wide, opens on row click.
//
// SHCT-737 — the panel is grouped into the sections §3.6 defines; SHCT-910 adds a fifth, 「3. 담당·연락」,
// and a header block above them (PRD 02 v1.35 §3.6). 키·체중 are gone (SHCT-737 v1.17): the values
// stopped being collected in 2026-06, so the panel was showing two fields nothing can ever fill.
//
// SHCT-910 · SHCT-981 — PRD 02 v1.37 §3.6 「전체 편집 모드」. The panel opens in 조회 모드; header [수정]
// turns every editable field into an input at once, [취소] discards the whole draft and [저장] validates
// every changed field together and commits them in ONE PUT (저장 1회 = 감사 로그 1건). It replaces the
// per-field inline editing of SHCT-737, whose unit of commit was a single field: §3.6 now makes the save
// a single audited event, and "click out to save" committed corrections nobody meant to make yet. The
// PUT still carries only the fields that changed, so a stale value is never replayed over someone else's
// correction — see `buildUpdateInput`.
//
// SHCT-910 A-1 — the panel is UNMASKED in both modes (§4.3 「마스킹 적용 범위 = 환자 목록으로 한정」).
// A masked value cannot be corrected, only overwritten blind; masking answers the many-patients-on-one-
// screen problem, which only the list has.
const PANEL_WIDTH = 480;

const GUARDIAN_RELATIONSHIPS: readonly PatientGuardianRelationship[] = [
  'SPOUSE',
  'CHILD',
  'PARENT',
  'SIBLING',
  'OTHER',
];

const GENDERS: readonly PatientGender[] = ['F', 'M'];

// One §3.6 section: a heading plus its own field grid, so the grouping is visible rather than implied
// by field order alone.
//
// SHCT-925 — PRD 02 §3.6 「섹션 표시 규격」. The title used to be 11px grey, the same size and colour as
// a field label, so it read as one more label and the sections ran together. It now takes the body size
// in the body colour with a left accent bar and a divider under it — the prototype's `sectionHtml()`,
// itself the same treatment as the registration modal's `.pm-section__title`.
function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 'var(--space-6)' }}>
      <h3
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 'var(--font-size-sm)',
          fontWeight: 700,
          color: 'var(--foreground)',
          margin: '0 0 var(--space-3)',
          paddingBottom: 'var(--space-2)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: 'inline-block',
            width: 3,
            height: 14,
            borderRadius: 2,
            background: 'var(--primary)',
          }}
        />
        {title}
      </h3>
      {/* The 2-column grid is set here, not left to `.detail-grid`: globals.css redefines that class
          further down as `140px 1fr` for `AdminPrototypePage`'s label/value layout, and that late rule
          wins. Fixing the global would move a screen this ticket does not touch. */}
      <div
        className="detail-grid"
        style={{ gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}
      >
        {children}
      </div>
    </section>
  );
}

interface DetailFieldProps {
  label: string;
  value: React.ReactNode;
  // Span both grid columns — for a value that is a list rather than a single line.
  span?: boolean;
  /** A validation message for this field (CLAUDE.md 7.1 — inline red text under the field). */
  error?: string;
}

function DetailField({ label, value, span, error }: DetailFieldProps) {
  return (
    <div
      className="detail-field"
      // SHCT-738 — PRD 02 v1.20 §3.6 puts 처방약 on a full-width row: it is a multi-entry list, and a
      // list inside one cell of the 2-column grid makes the neighbouring row's height jump with it.
      // Set here rather than as a new `globals.css` class — that file is shared by every screen and the
      // app is live on UAT, so a one-field layout need does not earn a global rule.
      style={span ? { gridColumn: '1 / -1' } : undefined}
    >
      <span className="detail-field__label">{label}</span>
      <span className="detail-field__value">{value}</span>
      {error && <p className="form-field__error">{error}</p>}
    </div>
  );
}

/**
 * The §5 편집 모드 lock marker — grey value + [잠금] + the reason as a tooltip.
 *
 * SHCT-981 QA — the reason is a CSS tooltip (`data-tip`, `globals.css` `.locked-value`), not a native
 * `title`: the native one waits ~500ms and is drawn by the OS, and QA read it as a tooltip with no text.
 * `aria-description` keeps the reason for screen readers, which `title` used to give.
 */
function LockedValue({ value, reason }: { value: React.ReactNode; reason: string }) {
  const { t } = useTranslation('admin');
  return (
    <span className="locked-value" data-tip={reason} aria-description={reason}>
      {value}
      <span role="img" aria-label={t('patientManagement.panel.locked')}>
        <Lock
          aria-hidden
          style={{ width: 11, height: 11, marginLeft: 4, opacity: 0.55, verticalAlign: -1 }}
        />
      </span>
    </span>
  );
}

/**
 * One of the 시스템 생성값 6종 (§3.6 「읽기 전용은 위 시스템 생성값 6종뿐이다」). In 조회 모드 it reads like
 * any other value; in 편집 모드 it stays a value — never an input — and explains itself by tooltip, since
 * §3.6 조건 분기 says a read-only field never enters edit mode. Every lock carries its own stated reason.
 */
function LockedField({
  label,
  value,
  reason,
  editing,
}: {
  label: string;
  value: React.ReactNode;
  reason: string;
  editing: boolean;
}) {
  return (
    <DetailField
      label={label}
      value={editing ? <LockedValue value={value} reason={reason} /> : value}
    />
  );
}

// The editing controls are the design system's own `form-field__input` / `form-field__select`, shrunk
// the way the prototype shrinks them (28px tall, 13px text) so an edit-mode row does not grow the grid.
// Only the compacting is inline; border, focus ring, background and chevron come from the shared classes.
const inputStyle: React.CSSProperties = {
  width: '100%',
  height: 28,
  fontSize: 13,
  padding: '0 6px',
  boxSizing: 'border-box',
};

// Same, plus room for the chevron the select class paints on the right.
const selectStyle: React.CSSProperties = {
  ...inputStyle,
  padding: '0 26px 0 6px',
  backgroundPosition: 'right 6px center',
  backgroundSize: '16px 16px',
};

const inputClass = (error?: string) =>
  `form-field__input${error ? ' form-field__input--error' : ''}`;
const selectClass = (error?: string) =>
  `form-field__select${error ? ' form-field__input--error' : ''}`;

// ── Edit-mode controls ─────────────────────────────────────────────────────

/**
 * 주진단 + 부진단 — two rows, one value. The backend validates the PAIR (the code must belong to the
 * group), and §3.6 조건 분기 resets 부진단 whenever 주진단 changes, so both rows edit `draft.diagnosis`.
 *
 * The detail response carries the diagnosis by NAME only, so the stored selection is resolved back to ids
 * from the master lists: the group by its name (five clinical domains, unique) and the code by its ICD
 * code (unique within a group). A name that does not resolve leaves the select on its placeholder rather
 * than guessing — and an untouched pair is never sent.
 */
function DiagnosisFields({
  patient,
  draft,
  error,
  onChange,
}: {
  patient: Patient;
  draft: PatientDraft;
  error?: string;
  onChange: (patch: Partial<PatientDraft>) => void;
}) {
  const { t } = useTranslation('admin');
  const f = (key: string) => t(`patientManagement.panel.fields.${key}`);
  const groupsQuery = useDiseaseGroupsQuery();
  const groups = useMemo(() => groupsQuery.data ?? [], [groupsQuery.data]);

  const touched = draft.diagnosis;
  const groupId = touched
    ? touched.groupId
    : (groups.find((g) => g.name === patient.mainDiagnosisName)?.id ?? null);
  const codesQuery = useDiseaseCodesQuery(groupId);
  const codes = useMemo(() => codesQuery.data ?? [], [codesQuery.data]);
  const codeId = touched
    ? touched.codeId
    : patient.diseaseCode
      ? (codes.find((c) => c.code === patient.diseaseCode)?.id ?? null)
      : null;

  return (
    <>
      <DetailField
        label={f('mainDiagnosis')}
        error={error}
        value={
          <select
            value={groupId ?? ''}
            onChange={(e) => {
              const next = groups.find((g) => g.id === Number(e.target.value));
              // A code belongs to exactly one group — §3.6: 옛 도메인의 질병코드가 남으면 안 된다.
              onChange({
                diagnosis: {
                  groupId: next?.id ?? null,
                  groupName: next?.name ?? null,
                  codeId: null,
                  code: null,
                },
              });
            }}
            aria-label={f('mainDiagnosis')}
            className={selectClass(error)}
            style={selectStyle}
          >
            {/* 주진단 is required — the placeholder renders for a record with no value but cannot be
                chosen. 부진단 below keeps its placeholder live: picking it is how 부진단 is removed. */}
            <option value="" disabled>
              {t('patientManagement.registrationModal.patient.primaryDiagnosisPlaceholder')}
            </option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        }
      />
      <DetailField
        label={f('subDiagnosis')}
        value={
          <select
            value={codeId ?? ''}
            disabled={groupId == null}
            onChange={(e) => {
              const picked = codes.find((c) => c.id === Number(e.target.value));
              onChange({
                diagnosis: {
                  groupId,
                  groupName: groups.find((g) => g.id === groupId)?.name ?? null,
                  codeId: picked?.id ?? null,
                  code: picked?.code ?? null,
                },
              });
            }}
            aria-label={f('subDiagnosis')}
            className="form-field__select"
            style={selectStyle}
          >
            <option value="">
              {t('patientManagement.registrationModal.patient.secondaryDiagnosisPlaceholder')}
            </option>
            {codes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} {c.name}
              </option>
            ))}
          </select>
        }
      />
    </>
  );
}

/**
 * 처방약 — a LIST of 약품명 + 복약주기 pairs (SHCT-737 QC ④ · SHCT-738). The row controls appear only
 * when the server proved it reads the list, by sending `medications` on the detail response: against a
 * server that still stores the superseded single pair, a second row would report a successful save and
 * lose the drug, which is worse than the missing feature.
 */
function MedicationRows({
  rows,
  supportsList,
  onChange,
}: {
  rows: PatientMedication[];
  supportsList: boolean;
  onChange: (rows: PatientMedication[]) => void;
}) {
  const { t } = useTranslation('admin');
  const setRow = (index: number, patch: Partial<PatientMedication>) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {rows.map((row, i) => (
        <span key={i} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <input
            type="text"
            value={row.name}
            placeholder={t('patientManagement.registrationModal.patient.medicationPlaceholder')}
            aria-label={t('patientManagement.registrationModal.patient.medication')}
            onChange={(e) => setRow(i, { name: e.target.value })}
            className="form-field__input"
            style={inputStyle}
          />
          <input
            type="text"
            value={row.freq}
            placeholder={t(
              'patientManagement.registrationModal.patient.medicationFrequencyPlaceholder',
            )}
            aria-label={t('patientManagement.registrationModal.patient.medicationFrequency')}
            onChange={(e) => setRow(i, { freq: e.target.value })}
            className="form-field__input"
            style={inputStyle}
          />
          {supportsList && rows.length > 1 && (
            <button
              type="button"
              className="btn btn--ghost"
              aria-label={t('patientManagement.panel.edit.medication.remove')}
              title={t('patientManagement.panel.edit.medication.remove')}
              onClick={() => onChange(rows.filter((_, at) => at !== i))}
            >
              −
            </button>
          )}
        </span>
      ))}
      {supportsList && (
        <button
          type="button"
          className="btn btn--ghost"
          style={{ alignSelf: 'flex-start' }}
          onClick={() => onChange([...rows, { name: '', freq: '' }])}
        >
          + {t('patientManagement.panel.edit.medication.add')}
        </button>
      )}
    </span>
  );
}

/** A 담당 의료진 name, or the placeholder for an assignment whose staff account was deleted. */
function StaffName({ name }: { name: string | null }) {
  const { t } = useTranslation('admin');
  if (name) return <>{name}</>;
  return (
    <span style={{ color: 'var(--muted-foreground)' }}>
      {t('patientManagement.panel.deletedStaff')}
    </span>
  );
}

/**
 * 담당 의료진 in 편집 모드 (SHCT-910 B-1 · SHCT-981). Same chips and the same search modal as the §3.7
 * registration form (opened by the parent): the FIRST entry is the 주담당, so a chip can be moved to the
 * front instead of being removed and re-added. Removing the 주담당 promotes the next one, as at
 * registration. The whole list is sent as one replacement, and only if it changed.
 */
function StaffEditor({
  staff,
  onChange,
  onAdd,
}: {
  staff: DraftStaff[];
  onChange: (staff: DraftStaff[]) => void;
  onAdd: () => void;
}) {
  const { t } = useTranslation('admin');
  const iconButton: React.CSSProperties = {
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    color: 'var(--muted-foreground)',
    display: 'inline-flex',
    fontSize: 11,
  };

  return (
    <span style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', alignItems: 'center' }}>
      {staff.map((s, i) => {
        const primary = i === 0;
        const name = s.name ?? t('patientManagement.panel.deletedStaff');
        return (
          <span
            key={s.id}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 10px',
              background: primary ? 'var(--badge-primary-bg)' : 'var(--secondary)',
              color: primary ? 'var(--badge-primary-text)' : 'var(--secondary-foreground)',
              borderRadius: 14,
              fontSize: 'var(--font-size-xs)',
            }}
          >
            <span
              style={{
                fontSize: 10,
                padding: '1px 5px',
                borderRadius: 3,
                background: 'var(--background)',
                fontWeight: 600,
              }}
            >
              {primary
                ? t('patientManagement.registrationModal.staff.primary')
                : t('patientManagement.registrationModal.staff.secondary')}
            </span>
            <StaffName name={s.name} />
            {/* A deleted account cannot become 주담당 — the server requires the 주담당 to be ACTIVE. */}
            {!primary && s.name != null && (
              <button
                type="button"
                style={iconButton}
                aria-label={t('patientManagement.panel.edit.staff.makePrimary', { name })}
                title={t('patientManagement.panel.edit.staff.makePrimary', { name })}
                onClick={() => onChange([s, ...staff.filter((other) => other.id !== s.id)])}
              >
                ↑
              </button>
            )}
            <button
              type="button"
              style={iconButton}
              aria-label={t('patientManagement.panel.edit.staff.remove', { name })}
              onClick={() => onChange(staff.filter((other) => other.id !== s.id))}
            >
              <X size={12} aria-hidden />
            </button>
          </span>
        );
      })}
      <button type="button" className="btn btn-sm btn-outline" onClick={onAdd}>
        <Plus size={12} aria-hidden /> {t('patientManagement.registrationModal.staff.add')}
      </button>
    </span>
  );
}

// ── Field grid ─────────────────────────────────────────────────────────────

// SHCT-910 — PRD 02 §3.6 「앱 미가입 환자의 표시」. A 본인-mode patient registered by staff but not yet
// signed up in the app has nothing in the values the app supplies, and a `-` there reads as 「입력할 수
// 있는데 안 넣음」. 보호자 mode is excluded (staff hand-enter 성별·생년월일 at registration), and so is an
// absent `appUserType` — the wire has not said which mode the patient is in.
function isNotJoined(patient: Patient): boolean {
  return patient.appUserType === 'patient' && patient.registeredAt == null;
}

/** Hyphenated for reading, never masked — the panel shows the stored number (§3.6 · §4.3). */
const phoneText = (phone: string | null | undefined) =>
  formatPhoneForDisplay(normalizePhone(phone));

type TextField =
  | 'name'
  | 'regNo'
  | 'phone'
  | 'residence'
  | 'residenceDetail'
  | 'guardianName'
  | 'guardianPhone';

/** What the grid needs to render 편집 모드. Absent = 조회 모드. */
export interface FieldGridEdit {
  draft: PatientDraft;
  errors: DraftErrors;
  onChange: (patch: Partial<PatientDraft>) => void;
  /** Opens the 담당 의료진 search modal (owned by the panel body, outside the grid). */
  onAddStaff: () => void;
}

// PRD 02 v1.35 §3.6 「섹션 구조」 — 기본 정보 / 진료 정보 / 담당·연락 / 앱·계정 / 이력.
//
// Only the fields the wire can actually supply are rendered. A row the server never sends is left out
// rather than stubbed with a permanent "-": a dash means "미입력" in this panel (§3.6), so using it for
// "the server does not send this" would tell the operator something nobody knows.
//
// Exported for the render tests: the guardian rows appear or vanish on a rule ([ADM.063 ②]) rather than
// on data, and the panel itself is only reachable through a query + a store.
export function PatientFieldGrid({ patient, edit }: { patient: Patient; edit?: FieldGridEdit }) {
  const { t } = useTranslation('admin');
  const editing = edit != null;
  const draft = edit?.draft;
  const errors = edit?.errors ?? {};
  const change = (patch: Partial<PatientDraft>) => edit?.onChange(patch);

  const diseaseText = patient.diseaseCode
    ? patient.diseaseName
      ? `${patient.diseaseCode} ${patient.diseaseName}`
      : patient.diseaseCode
    : '-';
  // [ADM.063 ①] — form ⓐ, 항목명 + 일시.
  const lastProText = patient.lastPro
    ? `${patient.lastPro.itemName} · ${formatDateTime(patient.lastPro.submittedAt)}`
    : '-';
  // SHCT-795 / SHCT-797 — PRD §3.6 gives every optional panel field the same empty state, "미입력 시 `-`",
  // and the wire also carries "" for some of them, so blank strings take the dash too.
  const residenceText = dashIfBlank(patient.residence);
  const residenceDetailText = dashIfBlank(patient.residenceDetail);
  const institutionText = dashIfBlank(patient.institution);
  // SHCT-738 [ADM.064 ②] — 주진단 shows the clinical domain name ALONE (e.g. `정신건강`).
  const mainDiagnosisText = dashIfBlank(patient.mainDiagnosisName);
  // SHCT-738 [ADM.064 ③] — 처방약 is a list of `약품명 · 복약주기`, kept in the order it was entered.
  const medications = medicationRows(patient).map((m) =>
    [m.name, m.freq].filter(Boolean).join(' · '),
  );
  // SHCT-794 — undefined (not a dash) while the wire omits the field.
  const appUserTypeText = patient.appUserType
    ? t(`patientManagement.panel.appUserTypes.${patient.appUserType}`)
    : null;
  const guardianMode = patient.appUserType === 'guardian';
  // SHCT-910 — only an EMPTY value becomes 미가입; a value that is present is shown as it is. Applied
  // to the six app-supplied fields alone — 의료진 입력 항목 keep the dash. Grey so it is not read as a value.
  const notJoined = isNotJoined(patient);
  const orNotJoined = (text: string): React.ReactNode =>
    notJoined && text === '-' ? (
      <span style={{ color: 'var(--muted-foreground)' }}>
        {t('patientManagement.panel.notJoined')}
      </span>
    ) : (
      text
    );
  const genderText = patient.gender ? t(`patientManagement.genders.${patient.gender}`) : '-';
  // SHCT-910 B-1 — `김의사 · 이간호사` in the server's order (주담당 first). §3.6 gives the 주담당 no mark
  // of its own: ADM.030 settled 「먼저 추가한 의료진이 주담당이 됩니다」, so the order IS the mark.
  const staff = patient.assignedStaff;
  const staffText: React.ReactNode =
    !staff || staff.length === 0
      ? '-'
      : staff.map((s, i) => (
          <span key={s.id}>
            {i > 0 && ' · '}
            <StaffName name={s.name} />
          </span>
        ));

  const f = (key: string) => t(`patientManagement.panel.fields.${key}`);
  const s = (key: string) => t(`patientManagement.panel.sections.${key}`);
  const lock = (key: string) => t(`patientManagement.panel.locks.${key}`);

  // A plain text input bound to one draft field.
  const textInput = (key: TextField, opts?: { phone?: boolean }) => (
    <input
      type="text"
      value={draft?.[key]}
      inputMode={opts?.phone ? 'numeric' : undefined}
      placeholder={opts?.phone ? t('patientManagement.panel.edit.phonePlaceholder') : undefined}
      aria-label={f(key)}
      onChange={(e) =>
        change({ [key]: opts?.phone ? toPhoneInputValue(e.target.value) : e.target.value })
      }
      className={inputClass(errors[key])}
      style={inputStyle}
    />
  );
  const dateInput = (key: 'birthDate' | 'dischargeDate') => (
    <input
      type="date"
      value={draft?.[key]}
      max={todayInKst()}
      aria-label={f(key)}
      onChange={(e) => change({ [key]: e.target.value })}
      className={inputClass(errors[key])}
      style={inputStyle}
    />
  );

  return (
    <>
      {/* SHCT-910 — 「1. 기본 정보」 holds identity alone: 이름 · 성별 · 생년월일. UID moved up to the
          header's summary line, which is where §3.6's section table leaves it. 성별·생년월일 are editable
          in BOTH modes since SHCT-981 — in 본인 mode they are NICE values, but NICE returns the phone
          OWNER's identity, so a family member's phone puts the wrong values on the record and the admin
          panel is the only place they can be put right (§3.6 편집 가능 필드). */}
      <DetailSection title={s('basic')}>
        <DetailField
          label={f('name')}
          error={errors.name}
          value={draft ? textInput('name') : dashIfBlank(patient.name)}
        />
        <DetailField
          label={f('gender')}
          error={errors.gender}
          value={
            draft ? (
              <select
                value={draft.gender}
                aria-label={f('gender')}
                onChange={(e) => change({ gender: e.target.value as PatientGender })}
                className={selectClass(errors.gender)}
                style={selectStyle}
              >
                {/* Required — the placeholder shows for a record with no value but cannot be chosen. */}
                <option value="" disabled>
                  {t('patientManagement.registrationModal.patient.genderPlaceholder')}
                </option>
                {GENDERS.map((g) => (
                  <option key={g} value={g}>
                    {t(`patientManagement.genders.${g}`)}
                  </option>
                ))}
              </select>
            ) : (
              orNotJoined(genderText)
            )
          }
        />
        <DetailField
          label={f('birthDate')}
          error={errors.birthDate}
          value={draft ? dateInput('birthDate') : orNotJoined(formatDate(patient.birthDate))}
        />
      </DetailSection>

      {/* SHCT-738 — PRD 02 v1.20 §3.6 「2. 진료 정보」, in the order the section table lists:
          주진단 · 부진단 · 처방약 · 치료기관 · 등록번호(MRN) · 퇴원일. [ADM.064 ①] — TWO diagnosis rows. */}
      <DetailSection title={s('clinical')}>
        {draft ? (
          <DiagnosisFields
            patient={patient}
            draft={draft}
            error={errors.diagnosis}
            onChange={change}
          />
        ) : (
          <>
            <DetailField label={f('mainDiagnosis')} value={mainDiagnosisText} />
            <DetailField label={f('subDiagnosis')} value={diseaseText} />
          </>
        )}
        <DetailField
          label={f('prescription')}
          span
          error={errors.medication}
          value={
            draft ? (
              <MedicationRows
                rows={draft.medication}
                supportsList={Array.isArray(patient.medications)}
                onChange={(medication) => change({ medication })}
              />
            ) : medications.length === 0 ? (
              '-'
            ) : (
              <ul className="detail-rx-list">
                {medications.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            )
          }
        />
        <LockedField
          label={f('institution')}
          value={institutionText}
          reason={lock('institution')}
          editing={editing}
        />
        {/* SHCT-910 — 등록번호(MRN) is editable: it is a number a person copies from the hospital, so it
            gets mistyped, and being the unique key is guarded by the duplicate check (409, 자기 자신
            제외) rather than by refusing the correction. */}
        <DetailField
          label={f('regNo')}
          error={errors.regNo}
          value={draft ? textInput('regNo') : dashIfBlank(patient.regNo)}
        />
        <DetailField
          label={f('dischargeDate')}
          error={errors.dischargeDate}
          value={draft ? dateInput('dischargeDate') : formatDate(patient.dischargeDate)}
        />
      </DetailSection>

      {/* SHCT-910 — 「3. 담당·연락」. 연락 수단끼리 묶으려고 환자 휴대폰·거주지·상세 주소를 기본 정보에서
          옮겨 왔다. 담당 의료진 행은 상세 응답이 목록을 보낼 때만 그린다 — 없을 때 `-` 를 그리면 「배정
          없음」이라는 거짓이 된다. */}
      <DetailSection title={s('contact')}>
        {Array.isArray(staff) && (
          <DetailField
            label={f('staff')}
            span
            error={errors.staff}
            value={
              draft ? (
                <StaffEditor
                  staff={draft.staff}
                  onChange={(next) => change({ staff: next })}
                  onAdd={() => edit?.onAddStaff()}
                />
              ) : (
                staffText
              )
            }
          />
        )}
        {/* SHCT-981 — 환자 휴대폰 is editable in both modes: app login finds the account by NICE DI, not
            by number, so a new number moves no account (§3.6 「휴대폰 번호 변경과 계정 연결」). Required in
            본인 mode, a clearable 선택 연락처 in 보호자 mode (§4.2). SHCT-794 (QC) — an absent number is `-`. */}
        <DetailField
          label={f('phone')}
          error={errors.phone}
          value={draft ? textInput('phone', { phone: true }) : phoneText(patient.phone)}
        />
        {/* SHCT-915 — 거주지와 상세 주소는 각각 한 행이고 둘 다 수정 대상이다(PRD 02 v1.23 §3.6). */}
        <DetailField
          label={f('residence')}
          error={errors.residence}
          value={draft ? textInput('residence') : orNotJoined(residenceText)}
        />
        <DetailField
          label={f('residenceDetail')}
          error={errors.residenceDetail}
          value={draft ? textInput('residenceDetail') : orNotJoined(residenceDetailText)}
        />
      </DetailSection>

      {/* SHCT-794 — PRD 02 v1.16 §3.6 「앱·계정」(v1.35 의 4번), resolved by [ADM.063].
          앱 사용자 유형 and the guardian block are rendered ONLY when the wire supplies them. */}
      <DetailSection title={s('appAccount')}>
        {appUserTypeText && (
          <LockedField
            label={f('appUserType')}
            value={appUserTypeText}
            reason={lock('appUserType')}
            editing={editing}
          />
        )}
        {/* [ADM.063 ②] — 본인 모드에서는 보호자 항목을 숨긴다. `-` 를 쓰지 않는 이유는 그것이
            "보호자가 있으나 데이터가 누락됐다"는 다른 뜻이 되기 때문이다. §3.7 등록 모달과 같은 처리. */}
        {guardianMode && (
          <>
            <DetailField
              label={f('guardianName')}
              error={errors.guardianName}
              value={draft ? textInput('guardianName') : dashIfBlank(patient.guardianName)}
            />
            <DetailField
              label={f('guardianRelationship')}
              error={errors.guardianRelationship}
              value={
                draft ? (
                  <select
                    value={draft.guardianRelationship}
                    aria-label={f('guardianRelationship')}
                    onChange={(e) =>
                      change({
                        guardianRelationship: e.target.value as PatientGuardianRelationship,
                      })
                    }
                    className={selectClass(errors.guardianRelationship)}
                    style={selectStyle}
                  >
                    <option value="" disabled>
                      {t('patientManagement.registrationModal.guardian.relationshipPlaceholder')}
                    </option>
                    {GUARDIAN_RELATIONSHIPS.map((r) => (
                      <option key={r} value={r}>
                        {t(`patientManagement.guardianRelationships.${r}`)}
                      </option>
                    ))}
                  </select>
                ) : patient.guardianRelationship ? (
                  t(`patientManagement.guardianRelationships.${patient.guardianRelationship}`)
                ) : (
                  '-'
                )
              }
            />
            {/* SHCT-981 — the number 보호자 mode matches at app signup; recorded here, never verified
                here (§3.6 「어드민에서는 인증하지 않는다」). */}
            <DetailField
              label={f('guardianPhone')}
              error={errors.guardianPhone}
              value={
                draft ? textInput('guardianPhone', { phone: true }) : phoneText(patient.guardianPhone)
              }
            />
          </>
        )}
        {/* [ADM.063 ③] — 「관리 상태」. 전환은 의료진웹 소관(§3.8)이라 읽기 전용이며, 상단 상태 칩과 같은
            값을 다시 보여주는 것은 의도된 중복이다. */}
        <LockedField
          label={f('careStatus')}
          value={t(`patientManagement.statusBadges.${patient.status}`)}
          reason={lock('careStatus')}
          editing={editing}
        />
      </DetailSection>

      {/* [ADM.063 ④] — 관리 상태 변경 이력은 별도 백로그 CR(SHCT-886)로 분리됐다. SHCT-910 — 회원가입일은
          「4. 앱·계정」에서 「5. 이력」으로 옮겼다: 시각 기록끼리 묶는다(PRD 02 v1.35 §3.6). */}
      <DetailSection title={s('history')}>
        <LockedField
          label={f('registeredAt')}
          value={orNotJoined(formatDate(patient.registeredAt))}
          reason={lock('systemGenerated')}
          editing={editing}
        />
        <LockedField
          label={f('lastPro')}
          value={orNotJoined(lastProText)}
          reason={lock('systemGenerated')}
          editing={editing}
        />
      </DetailSection>
    </>
  );
}

/**
 * SHCT-910 — PRD 02 §3.6 「헤더 블록」: the patient this panel is about, kept above the sections.
 *
 * <p>Row 1 is 이름 · 상태 칩 · 보호자 배지 and, on the right, the mode buttons (`actions`); row 2 is
 * `UID · 등록번호 · 성별 · 생년월일`. Row 2 is a read-only summary of the SAVED record, not an edit point —
 * each value is corrected in its section below — so it does not follow the draft. In 편집 모드 its UID
 * carries the system-generated lock, the one 읽기 전용 value that has no row of its own.
 *
 * <p>The name is the patient's own, unmasked (SHCT-910 A-1), even for a guardian-proxy patient — the
 * guardian's name has its own row in 「4. 앱·계정」.
 */
export function PanelHeader({
  patient,
  editing = false,
  actions,
}: {
  patient: Patient;
  editing?: boolean;
  actions?: React.ReactNode;
}) {
  const { t } = useTranslation('admin');
  const guardianMode = patient.appUserType === 'guardian';
  const notJoined = isNotJoined(patient);
  const orNotJoined = (text: string) =>
    notJoined && text === '-' ? t('patientManagement.panel.notJoined') : text;

  const uid = formatUid(patient.id);
  const rest = [
    dashIfBlank(patient.regNo),
    orNotJoined(patient.gender ? t(`patientManagement.genders.${patient.gender}`) : '-'),
    orNotJoined(formatDate(patient.birthDate)),
  ].join(' · ');

  return (
    <div
      style={{
        paddingBottom: 'var(--space-3)',
        marginBottom: 'var(--space-4)',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>
          {dashIfBlank(patient.name)}
        </span>
        <PatientStatusBadge patient={patient} />
        {guardianMode && (
          <span className="badge badge-primary" style={{ fontSize: 10, padding: '2px 6px' }}>
            {t('patientManagement.badges.guardian')}
          </span>
        )}
        {actions && (
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>{actions}</span>
        )}
      </div>
      <div
        style={{
          marginTop: 6,
          fontSize: 'var(--font-size-xs)',
          color: 'var(--muted-foreground)',
        }}
      >
        {editing ? (
          <>
            <LockedValue value={uid} reason={t('patientManagement.panel.locks.systemGenerated')} />
            {` · ${rest}`}
          </>
        ) : (
          `${uid} · ${rest}`
        )}
      </div>
      {patient.status === 'CLOSED' && patient.closedAt && (
        <div
          style={{
            marginTop: 4,
            fontSize: 'var(--font-size-xs)',
            color: 'var(--muted-foreground)',
          }}
        >
          {t('patientManagement.panel.closedAt', { date: formatDate(patient.closedAt) })}
        </div>
      )}
    </div>
  );
}

// ── Server rejections ──────────────────────────────────────────────────────
// The update endpoint answers with ONE code and no `error.fields` (api-spec PUT /admin/patients/{id}),
// so the field is derived from the code. CLAUDE.md 7.1 decides where each lands:
//   - a rule the value broke → inline red text under that field (the client checks the same rules first,
//     so these arrive only on a stale client or a paste the client let through);
//   - a duplicate / staff-state / mode / permission refusal → Modal, the business-error channel;
//   - anything else → the update-failed toast (system error).
// Wording is mapped from the CODE, never taken from the server text, so it stays KO/EN.

const REG = 'patientManagement.registrationModal.errors.';
const EDIT_ERR = 'patientManagement.panel.edit.errors.';

const FIELD_ERRORS: Partial<
  Record<PatientApiErrorCode, [DraftField, string, Record<string, unknown>?]>
> = {
  PATIENT_NAME_INVALID: ['name', `${REG}nameLength`],
  NAME_INVALID: ['name', `${REG}nameLength`],
  GENDER_REQUIRED: ['gender', `${REG}gender`],
  BIRTHDATE_REQUIRED: ['birthDate', `${REG}birthDate`],
  PATIENT_DOB_FUTURE_INVALID: ['birthDate', `${REG}birthDateFuture`],
  MRN_REQUIRED: ['regNo', `${REG}regNo`],
  PATIENT_MRN_INVALID: ['regNo', `${REG}regNoInvalid`],
  GUARDIAN_PHONE_INVALID: ['guardianPhone', `${REG}guardianPhone`],
  GUARDIAN_NAME_REQUIRED: ['guardianName', `${REG}guardianName`],
  GUARDIAN_NAME_INVALID: ['guardianName', `${REG}guardianNameLength`],
  GUARDIAN_RELATIONSHIP_REQUIRED: ['guardianRelationship', `${REG}guardianRelationship`],
  DISCHARGE_DATE_REQUIRED: ['dischargeDate', `${REG}dischargeDate`],
  DISCHARGE_DATE_FUTURE: ['dischargeDate', `${REG}dischargeDateFuture`],
  RESIDENCE_TOO_LONG: ['residence', `${EDIT_ERR}tooLong`, { max: RESIDENCE_MAX }],
  RESIDENCE_DETAIL_TOO_LONG: [
    'residenceDetail',
    `${EDIT_ERR}tooLong`,
    { max: RESIDENCE_DETAIL_MAX },
  ],
  PRIMARY_DIAGNOSIS_INVALID: ['diagnosis', `${EDIT_ERR}diagnosisInvalid`],
  SECONDARY_DIAGNOSIS_INVALID: ['diagnosis', `${EDIT_ERR}diagnosisInvalid`],
  SECONDARY_DIAGNOSIS_GROUP_MISMATCH: ['diagnosis', `${EDIT_ERR}diagnosisInvalid`],
  PATIENT_MEDICATION_INVALID: ['medication', `${EDIT_ERR}medicationInvalid`],
  // [ADM.030] backstop for the client-side 「1명 이상」 check — answered where the registration form does.
  PATIENT_PRIMARY_PHYSICIAN_REQUIRED: ['staff', `${REG}staff`],
};

const MODAL_ERRORS: Partial<Record<PatientApiErrorCode, string>> = {
  // 환자 번호 — the customer-confirmed copy the registration form uses (Confluence Q70). The guardian
  // number gets its own: on this path the collision may be with ANOTHER patient's patient OR guardian
  // number, which the registration wording (「이미 보호자 계정으로 사용 중」) does not cover.
  PATIENT_MOBILE_DUPLICATE: `${REG}phoneDuplicate`,
  GUARDIAN_MOBILE_DUPLICATE:
    'patientManagement.panel.edit.serverErrors.GUARDIAN_MOBILE_DUPLICATE',
  PATIENT_STAFF_INVALID: 'patientManagement.panel.edit.serverErrors.PATIENT_STAFF_INVALID',
  PATIENT_STAFF_DUPLICATE: 'patientManagement.panel.edit.serverErrors.PATIENT_STAFF_DUPLICATE',
  GUARDIAN_FIELD_NOT_APPLICABLE:
    'patientManagement.panel.edit.serverErrors.GUARDIAN_FIELD_NOT_APPLICABLE',
  INSUFFICIENT_PERMISSION: 'common:gate.noPermission.action',
};

/**
 * The panel body: header + grid + the edit session. Exported for the edit-mode tests, which drive it
 * with a fixed record instead of a query.
 *
 * <p>The session freezes the record it started from (`base`), so a background refetch cannot move the
 * baseline the diff compares against mid-edit. `onDirtyChange` reports whether the draft would save
 * anything, which is what the close confirmation asks about — entering edit mode alone is not a change.
 */
export function PatientDetailBody({
  patient,
  onDirtyChange,
}: {
  patient: Patient;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const { t } = useTranslation('admin');
  const { canWrite, denyProps } = useWriteGuard('PATIENT');
  const updatePatient = useUpdatePatient(patient.id);
  const [session, setSession] = useState<{ base: Patient; draft: PatientDraft } | null>(null);
  const [errors, setErrors] = useState<DraftErrors>({});
  // §3.6 조건 분기 — 퇴원일 변경은 저장 전에 PRO 재계산을 안내한다. 퇴원일은 `등록일자 = 치료시작일 =
  // PRO 기준일` 단일 필드라 값이 바뀌면 스케줄 전체가 다시 산정된다(의료진웹 03 §3.3과 동일 취급).
  const [pendingInput, setPendingInput] = useState<UpdatePatientInput | null>(null);
  const [blocked, setBlocked] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const dirty =
    session != null && Object.keys(buildUpdateInput(session.base, session.draft)).length > 0;
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const startEdit = () => {
    setSession({ base: patient, draft: draftFromPatient(patient) });
    setErrors({});
  };

  // [취소] and a successful save both land here: the whole draft goes, not field by field (§3.6).
  const endEdit = () => {
    setSession(null);
    setErrors({});
    setPendingInput(null);
    setPickerOpen(false);
  };

  const change = useCallback((patch: Partial<PatientDraft>) => {
    setSession((s) => (s ? { ...s, draft: { ...s.draft, ...patch } } : s));
    // Editing a field drops its message: it described a value that is no longer there.
    setErrors((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(patch) as DraftField[]) delete next[key];
      return next;
    });
  }, []);

  const report = (e: unknown) => {
    if (!(e instanceof PatientApiError)) {
      toast.error(t('patientManagement.toasts.updateError'));
      return;
    }
    if (e.code === 'PATIENT_DUPLICATE_MRN') {
      // §4.2 — 「이미 등록된 환자입니다. ({UID} {마스킹 이름} · {상태})」, built from the meta the 409 carries.
      setBlocked(
        t('patientManagement.toasts.mrnDuplicate', {
          pid: String(e.meta?.uid ?? ''),
          maskedName: String(e.meta?.maskedName ?? ''),
          statusLabel: String(e.meta?.statusLabel ?? ''),
        }),
      );
      return;
    }
    if (e.code === 'PHONE_INVALID') {
      const key =
        patient.appUserType === 'guardian' ? `${REG}phoneOptionalFormat` : `${REG}phone`;
      setErrors((prev) => ({ ...prev, phone: t(key) }));
      return;
    }
    const field = FIELD_ERRORS[e.code];
    if (field) {
      const [key, message, options] = field;
      setErrors((prev) => ({ ...prev, [key]: t(message, options) }));
      return;
    }
    const modal = MODAL_ERRORS[e.code];
    if (modal) {
      setBlocked(t(modal, { defaultValue: e.message }));
      return;
    }
    // The server's own message is not surfaced on purpose: a generic INTERNAL_ERROR body would replace
    // the PRD's wording with something an operator cannot act on.
    toast.error(t('patientManagement.toasts.updateError'));
  };

  const commit = (input: UpdatePatientInput) => {
    updatePatient.mutate(input, {
      onSuccess: () => {
        endEdit();
        toast.success(t('patientManagement.toasts.updateSuccess'));
      },
      onError: (e) => {
        // The draft stays as typed so the refused value can be corrected and saved again.
        setPendingInput(null);
        report(e);
      },
    });
  };

  const save = () => {
    if (!session || updatePatient.isPending) return;
    const found = validateDraft(session.base, session.draft, (key, options) =>
      t(key, options ?? {}),
    );
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }
    const input = buildUpdateInput(session.base, session.draft);
    if (Object.keys(input).length === 0) {
      endEdit();
      return;
    }
    if (input.dischargeDate !== undefined) {
      setPendingInput(input);
      return;
    }
    commit(input);
  };

  const actions = session ? (
    <>
      <button
        type="button"
        className="btn btn-outline btn-sm"
        onClick={endEdit}
        disabled={updatePatient.isPending}
      >
        {t('patientManagement.actions.cancel')}
      </button>
      <button
        type="button"
        className="btn btn-primary btn-sm"
        onClick={save}
        disabled={updatePatient.isPending}
      >
        {updatePatient.isPending
          ? t('patientManagement.actions.saving')
          : t('patientManagement.actions.save')}
      </button>
    </>
  ) : (
    // §3.6 조건 분기 · PRD 21 §7.4 P-2 — without `PATIENT:WRITE` the button stays visible but disabled
    // with the deny tooltip: an operator who cannot see it cannot learn a permission is what stops them.
    <button
      type="button"
      className="btn btn-outline btn-sm"
      onClick={startEdit}
      disabled={!canWrite}
      {...denyProps}
    >
      <Pencil aria-hidden style={{ width: 13, height: 13 }} />{' '}
      {t('patientManagement.actions.edit')}
    </button>
  );

  return (
    <>
      <PanelHeader patient={patient} editing={session != null} actions={actions} />

      <PatientFieldGrid
        patient={session?.base ?? patient}
        edit={
          session
            ? {
                draft: session.draft,
                errors,
                onChange: change,
                onAddStaff: () => setPickerOpen(true),
              }
            : undefined
        }
      />

      {session && (
        <PatientRegistrationStaffPicker
          open={pickerOpen}
          excludeIds={session.draft.staff.map((s) => s.id)}
          onSelect={(picked) => {
            change({ staff: [...session.draft.staff, { id: picked.id, name: picked.name }] });
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}

      <ConfirmModal
        open={pendingInput != null}
        title={t('patientManagement.panel.dischargeRecalc.title')}
        message={t('patientManagement.panel.dischargeRecalc.message')}
        confirmLabel={t('common:actions.confirm')}
        cancelLabel={t('patientManagement.actions.cancel')}
        loading={updatePatient.isPending}
        loadingLabel={t('patientManagement.actions.saving')}
        onConfirm={() => pendingInput && commit(pendingInput)}
        // Declining keeps the draft in edit mode — the operator may still want the other changes.
        onCancel={() => setPendingInput(null)}
      />

      <InfoModal
        open={blocked != null}
        title={t('patientManagement.panel.edit.blockedTitle')}
        body={blocked}
        confirmLabel={t('common:actions.confirm')}
        onClose={() => setBlocked(null)}
      />
    </>
  );
}

function PanelContent({
  id,
  onDirtyChange,
}: {
  id: number;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const { t } = useTranslation('admin');
  const { data, isLoading, refetch } = usePatientQuery(id);

  if (isLoading && !data) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted-foreground)' }}>
        {t('patientManagement.panel.loading')}
      </div>
    );
  }
  // Only when there is nothing to show: a failed BACKGROUND refetch keeps the record on screen, and with
  // it any edit session in progress — unmounting the body here would throw the draft away.
  if (!data) {
    return (
      <div
        role="alert"
        style={{
          padding: 16,
          border: '1px solid var(--destructive)',
          borderRadius: 8,
          background: '#FEE2E8',
          color: 'var(--destructive)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <span>{t('patientManagement.panel.loadError')}</span>
        <button type="button" className="btn btn-sm btn-outline" onClick={() => void refetch()}>
          {t('patientManagement.actions.retry')}
        </button>
      </div>
    );
  }
  return <PatientDetailBody patient={data} onDirtyChange={onDirtyChange} />;
}

export function PatientDetailPanel() {
  const { t } = useTranslation('admin');
  const openId = usePatientDetailStore((s) => s.openId);
  const closePanel = usePatientDetailStore((s) => s.close);
  const [dirty, setDirty] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);

  // Clicking another row swaps `openId` without closing the panel. The body is keyed by the id below, so
  // its edit session cannot carry a draft onto a different patient; this resets what lives up here.
  useEffect(() => {
    setDirty(false);
    setConfirmClose(false);
  }, [openId]);

  // §3.6 조건 분기 — 편집 모드에서 변경한 내용이 있으면 확인 후 닫는다. Without this the operator loses a
  // half-made correction to a stray click on the overlay with no way to know it happened.
  const requestClose = useCallback(() => {
    if (dirty) {
      setConfirmClose(true);
      return;
    }
    closePanel();
  }, [dirty, closePanel]);

  const discardAndClose = useCallback(() => {
    setConfirmClose(false);
    setDirty(false);
    closePanel();
  }, [closePanel]);

  // The SlidePanel title stays the fixed 「환자 정보」: the patient's name is already the header block's
  // first line, and the title is rendered before the detail query resolves.
  return (
    <>
      <SlidePanel
        open={openId != null}
        title={t('patientManagement.panel.title')}
        width={PANEL_WIDTH}
        onClose={requestClose}
      >
        {openId != null && <PanelContent key={openId} id={openId} onDirtyChange={setDirty} />}
      </SlidePanel>

      <ConfirmModal
        open={confirmClose}
        title={t('patientManagement.panel.discardOnClose.title')}
        message={t('patientManagement.panel.discardOnClose.message')}
        confirmLabel={t('patientManagement.panel.discardOnClose.confirm')}
        cancelLabel={t('patientManagement.panel.discardOnClose.cancel')}
        onConfirm={discardAndClose}
        onCancel={() => setConfirmClose(false)}
      />
    </>
  );
}
